import { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ExternalLink, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

function formatMinor(amount, currency) {
  const v = (Number(amount) || 0) / 100;
  const cur = (currency || '').toUpperCase();
  return `$${v.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
}

function formatEpoch(sec) {
  if (!sec) return '-';
  try { return new Date(sec * 1000).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }); } catch { return sec; }
}

const CHARGE_STATUS = {
  succeeded: { label: 'Exitoso', className: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' },
  failed: { label: 'Fallido', className: 'bg-red-100 text-red-700' },
};

export function StripeTab() {
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const [acctRes, balRes, chRes] = await Promise.all([
        adminAPI.getStripeAccount(),
        adminAPI.getStripeBalance(),
        adminAPI.getStripeCharges({ limit: 25 }),
      ]);
      setAccount(acctRes.data);
      setBalance(balRes.data);
      setCharges(chRes.data.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo conectar con Stripe';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(true); }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="stripe-tab">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700 text-sm" data-testid="stripe-error">
          {error}
        </div>
      )}

      {/* Account card */}
      {account && (
        <div className="bg-white border border-border rounded-md p-5" data-testid="stripe-account-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight">Cuenta Stripe</h3>
                <Badge className={account.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'} data-testid="stripe-mode-badge">
                  {account.mode === 'live' ? 'LIVE' : 'TEST'}
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                <InfoRow label="Nombre del negocio" value={account.business_profile?.name || '-'} />
                <InfoRow label="Email de contacto" value={account.email || '-'} />
                <InfoRow label="ID de cuenta" value={<code className="text-xs">{account.id}</code>} />
                <InfoRow label="País" value={account.country || '-'} />
                <InfoRow label="Moneda por defecto" value={(account.default_currency || '').toUpperCase()} />
                <InfoRow label="Cobros habilitados" value={<StatusIcon on={account.charges_enabled} />} />
                <InfoRow label="Payouts habilitados" value={<StatusIcon on={account.payouts_enabled} />} />
                <InfoRow label="Detalles enviados" value={<StatusIcon on={account.details_submitted} />} />
              </div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <Button variant="outline" size="sm" onClick={() => load(false)} disabled={refreshing} data-testid="stripe-refresh-btn">
                {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Actualizar
              </Button>
              {account.dashboard_url && (
                <a href={account.dashboard_url} target="_blank" rel="noopener noreferrer" data-testid="stripe-dashboard-link">
                  <Button className="btn-accent" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Abrir Stripe Dashboard
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Balance */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BalanceCard title="Disponible" entries={balance.available} testid="stripe-balance-available" />
          <BalanceCard title="Pendiente" entries={balance.pending} testid="stripe-balance-pending" />
        </div>
      )}

      {/* Live charges */}
      <div className="bg-white border border-border rounded-md">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight">Charges recientes ({charges.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stripe-charges-table">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Fecha</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Cliente</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Método</th>
                <th className="text-right py-3 px-4 font-semibold uppercase text-xs">Monto</th>
                <th className="text-right py-3 px-4 font-semibold uppercase text-xs">Reembolsado</th>
                <th className="text-left py-3 px-4 font-semibold uppercase text-xs">Estado</th>
                <th className="text-right py-3 px-4 font-semibold uppercase text-xs">Acción</th>
              </tr>
            </thead>
            <tbody>
              {charges.length === 0 && (
                <tr><td colSpan={7} className="py-8 px-4 text-center text-muted-foreground">Sin charges recientes en Stripe.</td></tr>
              )}
              {charges.map(ch => {
                const st = CHARGE_STATUS[ch.status] || { label: ch.status, className: 'bg-gray-100 text-gray-500' };
                return (
                  <tr key={ch.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`charge-row-${ch.id}`}>
                    <td className="py-3 px-4 whitespace-nowrap">{formatEpoch(ch.created)}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{ch.customer_name || '-'}</div>
                      <div className="text-xs text-muted-foreground">{ch.customer_email || '-'}</div>
                    </td>
                    <td className="py-3 px-4 uppercase text-xs">{ch.payment_method_type || '-'}</td>
                    <td className="py-3 px-4 text-right font-mono whitespace-nowrap">{formatMinor(ch.amount, ch.currency)}</td>
                    <td className="py-3 px-4 text-right font-mono whitespace-nowrap">{ch.amount_refunded ? formatMinor(ch.amount_refunded, ch.currency) : '-'}</td>
                    <td className="py-3 px-4"><Badge className={st.className}>{st.label}</Badge></td>
                    <td className="py-3 px-4 text-right">
                      {ch.receipt_url && (
                        <a href={ch.receipt_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs inline-flex items-center gap-1">
                          Recibo <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 py-1">
      <span className="text-xs uppercase text-muted-foreground">{label}:</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function StatusIcon({ on }) {
  return on ? <CheckCircle className="h-4 w-4 text-green-600 inline" /> : <XCircle className="h-4 w-4 text-red-500 inline" />;
}

function BalanceCard({ title, entries, testid }) {
  return (
    <div className="bg-white border border-border rounded-md p-4" data-testid={testid}>
      <div className="text-xs uppercase text-muted-foreground mb-2">{title}</div>
      {(!entries || entries.length === 0) ? (
        <div className="text-2xl font-bold">$0</div>
      ) : (
        <div className="space-y-1">
          {entries.map((e, i) => (
            <div key={i} className="text-2xl font-bold font-mono">
              {formatMinor(e.amount, e.currency)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
